import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
  Badge,
  Box,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  ScrollArea,
  Spinner,
  Text,
  type BadgeProps,
} from "@salestrak/ui";

import { useMemberDetail } from "#app/lib/hooks/use-member-detail";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useAppData } from "#app/lib/data";
import { usePermission } from "#app/lib/hooks/use-permission";
import { PageHeader, PageHeaderBack, PageHeaderTitle } from "#app/components/page-header";

import {
  ProfileForm,
  type DangerZoneConfig,
} from "#app/components/settings/profile-form";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

type PendingAction = "suspend" | "reactivate" | "cancel-invite";

// Badge tone is a single `variant`. Active carries the success tone (a soft
// wash — there is no solid success); the two neutral states share `outline`.
const MEMBER_STATUS_BADGE: Record<
  string,
  { label: string; variant: BadgeProps["variant"] }
> = {
  active: { label: "Active", variant: "success" },
  suspended: { label: "Suspended", variant: "outline" },
  invited: { label: "Invited", variant: "outline" },
};

function MemberProfilePage() {
  const { userId } = Route.useParams();
  const { replace } = useAppNavigate();
  const { currentUser } = useAppData();
  const { updateMember, resendInvite, cancelInvite } = useMemberMutations();
  const { user, loading, error } = useMemberDetail(userId);

  const canUpdateMembers = usePermission("manage.users");
  const canResendInvite = usePermission("manage.users");

  const [pendingAction, setPendingAction] = useState<PendingAction | null>(
    null,
  );

  const handleFieldSave = useCallback(
    async (fieldId: string, value: string) => {
      if (!userId) return;
      await updateMember(userId, { [fieldId]: value });
    },
    [userId, updateMember],
  );

  const memberLabel = user?.name?.trim() || user?.email || "";

  const handleConfirm = useCallback(async () => {
    if (!userId || !pendingAction) return;
    if (pendingAction === "cancel-invite") {
      const result = await cancelInvite(userId);
      if (!result) return;
      setPendingAction(null);
      replace("/settings/members");
      return;
    }
    await updateMember(userId, {
      memberStatus: pendingAction === "suspend" ? "suspended" : "active",
    });
    setPendingAction(null);
  }, [userId, pendingAction, cancelInvite, updateMember, replace]);

  const handleResendInvite = useCallback(async () => {
    if (!userId) return;
    await resendInvite(userId);
  }, [userId, resendInvite]);

  const isCurrentUser = currentUser?.userId === userId;
  const memberStatus = user?.memberStatus;

  const dangerZone = useMemo<DangerZoneConfig[]>(() => {
    if (!user) return [];
    const actions: DangerZoneConfig[] = [];

    if (memberStatus === "invited" && canResendInvite) {
      actions.push({
        id: "resend-invite",
        label: "Resend the invitation email",
        buttonLabel: "Resend invite",
        variant: "default",
        onClick: handleResendInvite,
      });
      actions.push({
        id: "cancel-invite",
        label: "Cancel this invitation",
        buttonLabel: "Cancel invite...",
        variant: "destructive",
        onClick: () => setPendingAction("cancel-invite"),
      });
    }

    if (memberStatus === "active" && canUpdateMembers && !isCurrentUser) {
      actions.push({
        id: "suspend",
        label: "Remove from the workspace",
        buttonLabel: "Suspend member...",
        variant: "destructive",
        onClick: () => setPendingAction("suspend"),
      });
    }

    if (memberStatus === "suspended" && canUpdateMembers) {
      actions.push({
        id: "reactivate",
        label: "Reactivate this member",
        buttonLabel: "Reactivate user...",
        variant: "default",
        onClick: () => setPendingAction("reactivate"),
      });
    }

    return actions;
  }, [
    user,
    memberStatus,
    canResendInvite,
    canUpdateMembers,
    isCurrentUser,
    handleResendInvite,
  ]);

  if (loading && !user) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Spinner />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box fill="flex" align="center" justify="center">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Unable to load member profile</EmptyTitle>
            {error ? (
              <EmptyDescription>{error.message}</EmptyDescription>
            ) : null}
          </EmptyHeader>
        </Empty>
      </Box>
    );
  }

  const dialogTitle =
    pendingAction === "suspend"
      ? `Suspend ${memberLabel}?`
      : pendingAction === "reactivate"
        ? `Reactivate ${memberLabel}?`
        : pendingAction === "cancel-invite"
          ? `Cancel invite for ${memberLabel}?`
          : "";

  const dialogDescription =
    pendingAction === "suspend"
      ? `${memberLabel} will no longer be able to access this workspace.`
      : pendingAction === "reactivate"
        ? `${memberLabel} will regain access to this workspace.`
        : pendingAction === "cancel-invite"
          ? `${memberLabel} will be removed from this workspace. The invitation link in their email will stop working.`
          : "";

  const dialogConfirmLabel =
    pendingAction === "suspend"
      ? "Suspend member"
      : pendingAction === "reactivate"
        ? "Reactivate member"
        : "Cancel invite";

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderBack label="Members" onClick={() => replace("/settings/members")} />
          <PageHeaderTitle
            adornment={
              memberStatus && MEMBER_STATUS_BADGE[memberStatus] ? (
                <Box>
                  <Badge variant={MEMBER_STATUS_BADGE[memberStatus].variant}>
                    <Text>{MEMBER_STATUS_BADGE[memberStatus].label}</Text>
                  </Badge>
                </Box>
              ) : undefined
            }
          >
            {user.name || user.email}
          </PageHeaderTitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="xl" fill="width" maxWidth={800}>
            <ProfileForm
              user={user}
              onFieldSave={handleFieldSave}
              dangerZone={dangerZone}
            />
          </Box>
        </Box>
      </ScrollArea>

      <AlertDialog
        open={!!pendingAction}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
      >
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{dialogTitle}</AlertDialogTitle>
            <AlertDialogDescription>{dialogDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="outline" />}>
              Cancel
            </AlertDialogClose>
            <Button
              variant={pendingAction === "reactivate" ? "default" : "destructive"}
              onClick={handleConfirm}
            >
              {dialogConfirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </Box>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/members/$userId",
)({ component: withPermission("manage.users", MemberProfilePage) });
